import { Button } from "@bcc/components/ui/button";
import { Calendar } from "@bcc/components/ui/calendar";
import { Label } from "@bcc/components/ui/label";
import { NumericInput } from "@bcc/components/ui/numeric-input";
import { Popover, PopoverContent, PopoverTrigger } from "@bcc/components/ui/popover";
import { Textarea } from "@bcc/components/ui/textarea";
import { useToast } from "@bcc/hooks/use-toast";
import { buildShipToCurrentValues } from "@bcc/lib/products/ship-to-countries";
import {
  DEFAULT_LONG_TERM_MAX_WEEKLY_INVENTORY,
  DEFAULT_LONG_TERM_MIN_WEEKLY_INVENTORY,
  DEFAULT_SHORT_TERM_MAX_WEEKLY_INVENTORY,
  DEFAULT_SHORT_TERM_MIN_WEEKLY_INVENTORY,
} from "@client-shared/lib/constants";
import { useConfigState } from "@client-shared/providers/config";
import { zodResolver } from "@hookform/resolvers/zod";
import type { CrossBorderCountries } from "@shared/schemas/enums/crossBorder.enum";
import type { ApprovalRequestResult, CountryApprovalValues, RoasterApprovalRequestDraft } from "@shared/services/salesforce/types";
import type { ShipToInfo, VendorCountryCode } from "@shared/types";
import { format } from "date-fns";
import { CalendarIcon, Loader2 } from "lucide-react";
import React, { useEffect, useState } from "react";
import type { UseFormReturn } from "react-hook-form";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { ApprovalRequestResults } from "./approval-request-results";
import { setWeeklyLimitPopoverTestId } from "./weekly-limit-popover.utils";
export type WeeklyLimitVariant = {
  id: string;
  size: string;
  weeklyLimit: number;
  sku: string;
  vendorKey: string;
  productName: string;
  coffeeTerm: string;
  // The roaster's own ISO country code (e.g. "DE", "NL", "US"). Becomes the
  // request's roasterCountryCode and the shipToCountryCode of the single
  // ship-to entry for non-cross-border SKUs.
  roasterCountryCode: VendorCountryCode;
  // Per-ship-to-country breakdown from the product fetch (channel → country),
  // carrying each country's current weekly inventory.
  shipToInfo?: ShipToInfo[];
  // The SKU's ship-to destination countries (e.g. ["NL", "DE"]). When present
  // the inventory update emits one entry per country; absent for non-cross-border
  // SKUs, which emit a single roaster-country entry.
  shipTo?: CrossBorderCountries[];
};

interface WeeklyLimitBounds {
  min: number;
  max: number;
}

/**
 * Bounds come from the config provider in production; fall back to the
 * client-shared defaults (same constants the client's VariantForm /
 * SingleVariantForm use) when the config hasn't loaded or a specific
 * term's min/max isn't populated. Guarantees the popover always has a
 * range to validate against and renders consistent helper text.
 */
const getWeeklyLimitBounds = (
  coffeeTerm: string,
  inventoryConfig?: {
    shortTermMinWeeklyInventory?: number;
    shortTermMaxWeeklyInventory?: number;
    longTermMinWeeklyInventory?: number;
    longTermMaxWeeklyInventory?: number;
  }
): WeeklyLimitBounds => {
  if (coffeeTerm === "Short") {
    return {
      min: inventoryConfig?.shortTermMinWeeklyInventory ?? DEFAULT_SHORT_TERM_MIN_WEEKLY_INVENTORY,
      max: inventoryConfig?.shortTermMaxWeeklyInventory ?? DEFAULT_SHORT_TERM_MAX_WEEKLY_INVENTORY,
    };
  }
  return {
    min: inventoryConfig?.longTermMinWeeklyInventory ?? DEFAULT_LONG_TERM_MIN_WEEKLY_INVENTORY,
    max: inventoryConfig?.longTermMaxWeeklyInventory ?? DEFAULT_LONG_TERM_MAX_WEEKLY_INVENTORY,
  };
};

type WeeklyLimitTranslation = (key: string, options?: Record<string, number | string>) => string;

const createSchema = (t: WeeklyLimitTranslation, weeklyLimitBounds: WeeklyLimitBounds) =>
  z
    .object({
      // Integer + positive + range checks all live in superRefine below so
      // the i18n `positiveInteger` / `weeklyLimitRange` messages reach the
      // user. A base `.int()` here would surface zod's default "Expected
      // integer" message first and swallow the custom copy.
      weeklyLimit: z.number().optional().nullable(),
      effectiveDate: z.date().optional(),
      notes: z.string().optional(),
    })
    .superRefine((val, ctx) => {
      const { min, max } = weeklyLimitBounds;
      if (typeof val.weeklyLimit !== "number") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["weeklyLimit"],
          message: t("products.weeklyLimit.validation.weeklyLimitRequired"),
        });
      } else if (!Number.isInteger(val.weeklyLimit) || val.weeklyLimit <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["weeklyLimit"],
          message: t("products.weeklyLimit.validation.positiveInteger"),
        });
      } else if (val.weeklyLimit < min || val.weeklyLimit > max) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["weeklyLimit"],
          message: t("products.weeklyLimit.validation.weeklyLimitRange", {
            min,
            max,
          }),
        });
      }

      if (!val.effectiveDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["effectiveDate"],
          message: t("products.weeklyLimit.validation.effectiveDateRequired"),
        });
      }
      if (!val.notes?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["notes"],
          message: t("products.weeklyLimit.validation.notesRequired"),
        });
      }
    });

type SchemaType = ReturnType<typeof createSchema>;
export type WeeklyLimitFormValues = z.infer<SchemaType>;

interface WeeklyLimitPopoverProps {
  variant: WeeklyLimitVariant;
  onUpdateWeeklyLimit: (request: RoasterApprovalRequestDraft) => Promise<ApprovalRequestResult[]>;
  isUpdating?: boolean;
  /**
   * Fires once the provider resolves a successful weekly-limit update.
   * Parent cells use this to lock their trigger button out until the
   * next page refresh, preventing duplicate approval requests.
   */
  onSubmitSuccess?: () => void;
  children: React.ReactNode;
  testId?: string;
  onFormReady?: (form: UseFormReturn<WeeklyLimitFormValues>) => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const isSunday = (date: Date) => date.getDay() === 0;

const disableNonSundays = (date: Date) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (date <= today) return true;
  if (!isSunday(date)) return true;
  return false;
};

export function WeeklyLimitPopover({
  variant,
  onUpdateWeeklyLimit,
  isUpdating,
  onSubmitSuccess,
  children,
  testId,
  onFormReady,
  open: controlledOpen,
  onOpenChange,
}: Readonly<WeeklyLimitPopoverProps>) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [{ config }] = useConfigState();
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = controlledOpen ?? uncontrolledOpen;
  const [effectiveDateOpen, setEffectiveDateOpen] = useState(false);

  const handleOpenChange = (next: boolean) => {
    if (controlledOpen === undefined) setUncontrolledOpen(next);
    onOpenChange?.(next);
    if (!next) setEffectiveDateOpen(false);
  };

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- collapse effective-date sub-popover when the parent popover closes
    if (!open) setEffectiveDateOpen(false);
  }, [open]);

  const weeklyLimitBounds = React.useMemo(
    () => getWeeklyLimitBounds(variant.coffeeTerm, config.inventoryConfig),
    [config.inventoryConfig, variant.coffeeTerm]
  );

  const schema = React.useMemo(() => createSchema(t as WeeklyLimitTranslation, weeklyLimitBounds), [t, weeklyLimitBounds]);

  const form = useForm<WeeklyLimitFormValues>({
    resolver: zodResolver(schema),
    // "all" validates on blur AND change and keeps formState.errors in
    // sync for every field, not just the one being edited — required so
    // the isValid gate and the required-field messages stay accurate
    // when a sibling field (superRefine) turns invalid.
    mode: "all",
    defaultValues: {
      weeklyLimit: variant.weeklyLimit,
      effectiveDate: undefined,
      notes: "",
    },
  });

  useEffect(() => {
    onFormReady?.(form);
    // onFormReady is stable (defined once at story level); form is stable per mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const watchedWeeklyLimit = useWatch({ control: form.control, name: "weeklyLimit" });
  // Guards against submitting a no-op request: the roaster must actually move
  // the limit away from the current value, not just re-enter it with new notes/date.
  const hasWeeklyLimitChanged = watchedWeeklyLimit !== variant.weeklyLimit;

  useEffect(() => {
    if (open) {
      form.reset({
        weeklyLimit: variant.weeklyLimit,
        effectiveDate: undefined,
        notes: "",
      });
      // Run the resolver once on open so formState.isValid and the
      // required-field errors reflect the empty form before the user
      // interacts. mode: "all" keeps them in sync after that.
      void form.trigger();
    }
    // reset on reopen to clear stale draft values
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onSubmit = async (values: WeeklyLimitFormValues) => {
    if (typeof values.weeklyLimit !== "number" || !values.effectiveDate) return;
    const newWeeklyInventory = String(values.weeklyLimit);
    // One ship-to entry per destination country (cross-border) or a single
    // roaster-country entry, each carrying the new and existing weekly inventory.
    const shipToCountries: CountryApprovalValues[] = buildShipToCurrentValues(
      variant.shipTo ?? [],
      variant.shipToInfo,
      variant.roasterCountryCode,
      { weeklyInventory: variant.weeklyLimit }
    ).map((entry) => ({
      shipToCountryCode: entry.countryCode,
      weeklyInventory: newWeeklyInventory,
      currentWeeklyInventory: String(entry.currentWeeklyInventory ?? variant.weeklyLimit),
      webStatus: entry.currentWebStatus,
    }));
    const request: RoasterApprovalRequestDraft = {
      actionType: "Inventory Update",
      vendorKey: variant.vendorKey,
      roasterCountryCode: variant.roasterCountryCode,
      itemNumbers: [variant.sku],
      roasterComments: values.notes?.trim() ?? "",
      coffeeTerm: variant.coffeeTerm,
      productName: variant.productName,
      roasterEffectiveDate: format(values.effectiveDate, "yyyy-MM-dd"),
      shipToCountries,
    };
    try {
      const results = await onUpdateWeeklyLimit(request);
      toast({
        title: t("products.weeklyLimit.submitSuccess"),
        description:
          results.length > 0 ? (
            <span>
              <span>{t("products.approvalRequest.submitSuccessDetail")}</span>
              <ApprovalRequestResults results={results} />
            </span>
          ) : undefined,
        variant: "success",
      });
      onSubmitSuccess?.();
      handleOpenChange(false);
    } catch {
      toast({
        title: t("common.error", { defaultValue: "Error" }),
        description: t("products.weeklyLimit.submitError"),
        variant: "destructive",
      });
    }
  };

  const preventClose = React.useCallback((e: Event) => e.preventDefault(), []);

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        testId={testId ?? setWeeklyLimitPopoverTestId("content")}
        className="w-80"
        align="start"
        onInteractOutside={preventClose}
        onEscapeKeyDown={preventClose}
      >
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <h4 className="font-medium text-sm">{t("products.weeklyLimit.title")}</h4>
            <p className="text-xs text-muted-foreground">{t("products.weeklyLimit.description", { size: variant.size })}</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="weeklyLimit" className="text-xs">
              {t("products.weeklyLimit.weeklyLimit")}
              <span aria-hidden="true" className="ml-0.5 text-destructive">
                *
              </span>
            </Label>
            <Controller
              control={form.control}
              name="weeklyLimit"
              render={({ field }) => (
                <NumericInput
                  id="weeklyLimit"
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  name={field.name}
                  placeholder="300"
                />
              )}
            />
            <p className="text-xs text-muted-foreground" data-testid={setWeeklyLimitPopoverTestId("range-helper")}>
              {t("products.weeklyLimit.weeklyLimitHelper", {
                min: weeklyLimitBounds.min,
                max: weeklyLimitBounds.max,
              })}
            </p>
            {form.formState.errors.weeklyLimit && <p className="text-xs text-destructive">{form.formState.errors.weeklyLimit.message}</p>}
          </div>

          <div className="space-y-2">
            <Label className="text-xs">
              {t("products.weeklyLimit.effectiveDate")}
              <span aria-hidden="true" className="ml-0.5 text-destructive">
                *
              </span>
            </Label>
            <Controller
              control={form.control}
              name="effectiveDate"
              render={({ field }) => (
                <Popover open={effectiveDateOpen} onOpenChange={setEffectiveDateOpen} modal>
                  <PopoverTrigger asChild>
                    <Button type="button" variant="outline" className="w-full justify-start text-left font-normal bg-transparent">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {field.value ? format(field.value, "PPP") : t("products.weeklyLimit.selectDate")}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value}
                      onSelect={(date) => {
                        field.onChange(date);
                        setEffectiveDateOpen(false);
                      }}
                      disabled={disableNonSundays}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              )}
            />
            {form.formState.errors.effectiveDate && (
              <p className="text-xs text-destructive">{form.formState.errors.effectiveDate.message}</p>
            )}
            <p className="text-xs text-muted-foreground">{t("products.weeklyLimit.sundaysOnly")}</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes" className="text-xs">
              {t("products.weeklyLimit.notes")}
              <span aria-hidden="true" className="ml-0.5 text-destructive">
                *
              </span>
            </Label>
            <Textarea id="notes" placeholder={t("products.weeklyLimit.notesPlaceholder")} {...form.register("notes")} rows={3} />
            {form.formState.errors.notes && <p className="text-xs text-destructive">{form.formState.errors.notes.message}</p>}
          </div>

          {form.formState.errors.root && <p className="text-xs text-destructive">{form.formState.errors.root.message}</p>}

          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => handleOpenChange(false)} className="flex-1">
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={!form.formState.isValid || !hasWeeklyLimitChanged || isUpdating}
              className="flex-1 bg-primary"
            >
              {isUpdating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isUpdating ? t("products.weeklyLimit.submitting") : t("products.weeklyLimit.submitForApproval")}
            </Button>
          </div>
        </form>
      </PopoverContent>
    </Popover>
  );
}
